import { HttpClient }          from '@angular/common/http';
import {
	Injectable,
	Signal,
	signal,
	WritableSignal
}                              from '@angular/core';
import { APP_CONFIG }          from '@appConfig/_app-config_master';
import { APP_MODE }            from '@appConfig/app-mode-config';
import { LANGUAGE_CONFIG }     from '@appConfig/language-config';
import {
	RequestCompleteBaseState,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                              from '@core/interfaces/api.interface';
import {
	ITranslationFile,
	LanguageEnum
}                              from '@models/language';
import { TranslateService }    from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoggerService }       from '@shared/services/logger.service';
import {
	actionStatus,
	ToasterService
}                              from '@shared/services/toaster.service';
import {
	ObjectUtils,
	StringUtils
}                              from '@utils/helpers';
import {
	forkJoin,
	Observable
}                              from 'rxjs';
import {
	map,
	take
}                              from 'rxjs/operators';


@Injectable( { providedIn: 'root' } )
export class TranslationService {

	private readonly _defaultLang = LanguageEnum.ENGLISH;
	private _locale: string;
	private readonly _translationServiceInitializedSignal: WritableSignal<boolean> = signal( false );

	get translationServiceInitializedSignal(): Signal<boolean> {
		return this._translationServiceInitializedSignal.asReadonly();
	}

	constructor(
		private readonly _httpClient: HttpClient,
		private readonly _logger: LoggerService,
		private readonly _translateService: TranslateService
	) {}

	private changeLanguage( language: string ) {
		this._logger.debug( `${ this.constructor.name }:changeLanguage language`, language );
		const supportedLanguagesRegEx = new RegExp( LANGUAGE_CONFIG.supportedLanguages.join( '|' ) );
		language = supportedLanguagesRegEx.test( language ) ? language : this._translateService.defaultLang;
		this._logger.debug( `${ this.constructor.name }:changeLanguage language after support check`, language );

		this._translateService.use( language );
	}

	private checkIfIsTranslatedAndSpare(
		i18nStr: string,
		translatedString: string,
		checkIfIsTranslated: boolean = true,
		spareString?: string
	): string {
		/*
		 * TODO This needs revision!
		 *  `TranslateService.instant` returns an empty string if it didn't find an
		 *  entry in the i18n maps matching the path.
		 *  Hence the logic below this block comment won't be executed and no spare
		 *  string would be returned, even if one was passed as parameter at the
		 *  bottom of the call stack!
		 *  Currently, this behavior requires enclosing `getI18nString()` into
		 *  `replaceEmptyValue()`!
		 *
		 * if ( !translatedString && spareString ) {
		 *  return spareString;
		 * }
		 */
		// if string is not translated, set empty string
		if ( checkIfIsTranslated && translatedString === i18nStr ) {
			let warningMessage = `getI18nString => string has not been found or is invalid ( ${ i18nStr } )`;
			this.i18nWarningsToDeveloper( warningMessage );
			translatedString = '';
			if ( spareString ) {
				warningMessage += `, and has been replaced with: ( ${ spareString } )`;
				this.i18nWarningsToDeveloper( warningMessage );
				translatedString = spareString;
			}
		}

		return translatedString;
	}

	private i18nWarningsToDeveloper( warningMessage: string = '', i18nStr: string = '' ): void {
		if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) {
			this._logger.warn( `${ warningMessage }\ni18nString:${ i18nStr }` );
		}
	}

	private isInvalidTranslationString( translatedString: string, i18nStr: string ): boolean {
		return !translatedString || translatedString === i18nStr || translatedString === '' || typeof translatedString !== 'string';
	}

	private overrideTranslationFile(
		languageId: string,
		originalTranslationData,
		overrideTranslationData
	): void {
		// exit when no override file exists or object is empty
		if (
			!ObjectUtils.isObject( overrideTranslationData ) ||
			Object.keys( overrideTranslationData ).length === 0
		) {
			return;
		}

		// maps override object structure to a flat path array
		const paths = ObjectUtils.recursivelyGetAllObjectsKeyPaths( overrideTranslationData );

		// processes each path element
		paths.forEach( path => {

			// reads value from path of overrideTranslationData
			const value = ObjectUtils.deepRead( overrideTranslationData, path );

			// writes value to originalTranslationData on path and appends if originalTranslationData doesn't contain override path
			ObjectUtils.deepWrite( originalTranslationData, path, value, true );
		} );

		// updates language
		this._translateService.setTranslation( languageId, originalTranslationData );
	}

	private processTranslationFileOverriding( toaster: ToasterService ): void {
		// loader for translation files for overwriting
		function customI18nLoaderFactory( lang: string, httpClient: HttpClient ): Observable<ITranslationFile> {
			return new TranslateHttpLoader( httpClient, LANGUAGE_CONFIG.languagesOverridePath, '.json' ).getTranslation( lang );
		}

		const languageIds: string[] = [];
		const languageDataList: Observable<ITranslationFile>[] = [];

		// process supported languages
		this._translateService.getLangs().forEach( ( languageId: string ) => {
			// requests for original and override translation language file
			const originalTranslationData$ = this._translateService.getTranslation( languageId );
			const overrideTranslationData$ = customI18nLoaderFactory( languageId, this._httpClient );

			languageIds.push( languageId );
			languageDataList.push( originalTranslationData$, overrideTranslationData$ );
		} );


		if ( languageIds.length ) {
			// requests all (original & override) language files
			forkJoin( [ ...languageDataList ] )
			.pipe( take( 1 ) )
			.subscribe( {
				next: ( languageDataList: object[] ) => {

					// process language file overriding
					languageDataList.forEach( ( languageData: object, index: number ) => {
						// languageDataList array is structured as an even/odd language pair
						// even index = originalTranslationData & odd index = overrideTranslationData
						if ( index % 2 === 0 ) {
							const languageId = languageIds[ index / 2 ] ?? null;
							const originalTranslationData = languageDataList[ index ] ?? {};
							const overrideTranslationData = languageDataList[ index + 1 ] ?? {};
							this.overrideTranslationFile( languageId, originalTranslationData, overrideTranslationData );
						}
					} );
				},
				error: ( error ) => {
					if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) {
						toaster.error( `Missing translations file, please check the error log.`, 'DEVELOPMENT ERROR' );
						this._logger.error( 'Missing translation file error', error );
					}
					// Observable will not complete in case of error!
					this._translationServiceInitializedSignal.set( true );
				},
				complete: () => {
					this._translationServiceInitializedSignal.set( true );
				}
			} );

			return;
		}

		this._translationServiceInitializedSignal.set( true );
	}

	public getI18nString(
		useTranslationPath: boolean,
		baseTranslationPath: string,
		translationPath: string,
		checkIfIsTranslated: boolean = true,
		spareString: string = ''
	): string {
		const i18nStr = useTranslationPath ? `${ baseTranslationPath }.${ translationPath }` : translationPath;

		if ( !i18nStr ) {
			this.i18nWarningsToDeveloper( 'getI18nString => there are no i18nPath arguments (' + i18nStr + ')' );
			return spareString;
		}

		const translatedString: string = this._translateService.instant( i18nStr );

		return this.checkIfIsTranslatedAndSpare( i18nStr, translatedString, checkIfIsTranslated, spareString );
	}

	public getI18nStringByArray(
		options: {
			translationPathsList: string[],
			checkIfIsTranslated?: boolean,
			spareString?: string
		} ): string[] {

		const {
			checkIfIsTranslated = true,
			spareString = '',
			translationPathsList
		} = options;


		const translatedResultList: string[] = [];
		translationPathsList.map( ( translationPath: string ) => {
			const translatedString: string = this.getI18nString(
				false,
				null,
				translationPath,
				checkIfIsTranslated,
				spareString
			);

			translatedResultList.push( translatedString );
		} );
		return translatedResultList;
	}

	public getI18nStringParams(
		useTranslationPath: boolean,
		baseTranslationPath: string,
		translationPath: string,
		params?: {},
		checkIfIsTranslated: boolean = true,
		spareString: string = ''
	): string {
		const i18nStr = useTranslationPath ? `${ baseTranslationPath }.${ translationPath }` : translationPath;

		if ( !i18nStr ) {
			this.i18nWarningsToDeveloper( 'getI18nStringParams => there are no i18nPath arguments (' + i18nStr + ')' );
			return spareString;
		}

		const translatedString: string = this._translateService.instant( i18nStr, params );

		return this.checkIfIsTranslatedAndSpare( i18nStr, translatedString, checkIfIsTranslated, spareString );
	}

	public getI18nObservable(
		useTranslationPath: boolean,
		baseTranslationPath: string,
		translationPath: string,
		checkIfIsTranslated: boolean = true,
		spareString: string = ''
	): Observable<any> {
		const i18nStr = useTranslationPath ? `${ baseTranslationPath }.${ translationPath }` : translationPath;
		return this._translateService.get( i18nStr )
		.pipe(
			map( ( translatedString: string ) =>
				this.checkIfIsTranslatedAndSpare( i18nStr, translatedString, checkIfIsTranslated, spareString )
			)
		);
	}

	public getApiRequestStateMessage(
		state: RequestCompleteBaseState<any> | RequestCompleteEntityState<any> | RequestCompleteListState<any>,
		entityName: string,
		action: actionStatus,
		onlyError?: boolean,
		showDefaultBeforeApiErrorMessage?: boolean
	): string {

		let i18nStr: string;
		let translatedString: string;

		if ( state?.requestState === RequestState.SUCCESS && action !== actionStatus.LIST && onlyError !== true ) {
			i18nStr = `success.${ entityName }.${ action }.text`;
			translatedString = this._translateService.instant( i18nStr );

			return this.isInvalidTranslationString( translatedString, i18nStr )
						 ? null
						 : translatedString;
		}

		if ( state?.requestState === RequestState.FAILED ) {

			// error message by error code :  error.{entityName}.{action}.{errorCode}
			if ( state?.error?.error?.code ) {
				i18nStr = `error.${ entityName }.${ action }.${ StringUtils.toCamelCase( state.error.error.code ) }`;
				translatedString = this._translateService.instant( i18nStr );
			}

			// error message by status and error code :  error.{entityName}.{action}.text-{status}.{errorCode}
			if ( state?.error?.error?.code && this.isInvalidTranslationString( translatedString, i18nStr ) ) {
				i18nStr = `error.${ entityName }.${ action }.text-${ state.error.status }.${ StringUtils.toCamelCase( state.error.error.code ) }`;
				translatedString = this._translateService.instant( i18nStr );
			}

			// error message by status, case 1 fallback :  error.{entityName}.{action}.text-{status}.text
			if ( this.isInvalidTranslationString( translatedString, i18nStr ) ) {
				this.i18nWarningsToDeveloper( 'missing translation of Backend error code. Please update i18n file(s).', i18nStr );

				i18nStr = `error.${ entityName }.${ action }.text-${ state.error.status }.text`;
				translatedString = this._translateService.instant( i18nStr );
			}

			// error message by status, case 2 fallback :  error.{entityName}.{action}.text-{status}
			if ( this.isInvalidTranslationString( translatedString, i18nStr ) ) {
				this.i18nWarningsToDeveloper( 'missing translation error message for status case 1 fallback. Please update i18n file(s).', i18nStr );

				i18nStr = `error.${ entityName }.${ action }.text-${ state.error.status }`;
				translatedString = this._translateService.instant( i18nStr );
			}

			// fallback to default text error :  error.{entityName}.{action}.text
			if ( showDefaultBeforeApiErrorMessage && this.isInvalidTranslationString( translatedString, i18nStr ) ) {
				this.i18nWarningsToDeveloper( 'missing translation error message for status case 2 fallback. Please update i18n file(s).', i18nStr );

				i18nStr = `error.${ entityName }.${ action }.text`;
				translatedString = this._translateService.instant( i18nStr );
			}

			// fallback to api error message
			if ( this.isInvalidTranslationString( translatedString, i18nStr ) ) {
				const errorMessage = (
															 !state?.error?.error?.message || state?.error?.error?.message !== ''
														 )
														 ? state?.error?.error?.message
														 : this._translateService.instant( 'error.shared.noBackendErrorMessageFallback' );


				// informs developer about missing translation for status error
				this.i18nWarningsToDeveloper( 'missing translation of Backend status error. Please update i18n file(s).', i18nStr );
				return errorMessage; // shows BE error message if no translation is found

			} else {
				return translatedString; // shows translated error message
			}
		}
	}

	public setupLanguage( toaster: ToasterService ): void {
		// language pre-setup (prerequisite for language overrides)
		this._translateService.addLangs( LANGUAGE_CONFIG.supportedLanguages );
		this._translateService.setDefaultLang( this._defaultLang );
		this.changeLanguage( this._defaultLang );

		// language overrides
		this.processTranslationFileOverriding( toaster );
	}

	public setUserLocale( locale: string[] ): void {
		// currentUsers language preference
		if ( locale?.length > 0 ) {
			this._locale = locale[ 0 ];
			this._logger.debug( 'language keycloak: ', this._locale );
		} else { // fallback to browser language setting
			this._locale = this._translateService.getBrowserLang();
			this._logger.debug( 'language getBrowserLang: ', this._locale );
		}

		// applies language if supported
		if ( LANGUAGE_CONFIG.supportedLanguages.includes( this._locale as LanguageEnum ) ) {
			this._translateService.use( this._locale );
			this.changeLanguage( this._locale );
		}
	}
}
